import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiSlash } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { getFlagUrl } from '../utilities/flags';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import {
  DISQ_PAGE_SIZE,
  extractList,
  formatDisqualificationTerm,
  isAutoSettingConfigured,
  isManualDisqualification,
} from '../utilities/disqualifications';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';

function LeagueFlag({ flag }) {
  const [failed, setFailed] = useState(false);
  const src = getFlagUrl(flag);

  if (!src || failed) return null;

  return (
    <img
      src={src}
      alt=""
      className="dq-league-flag"
      onError={() => setFailed(true)}
    />
  );
}

export default function LeagueDisqualificationsManagement() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [autoLeagues, setAutoLeagues] = useState([]);
  const [autoLoading, setAutoLoading] = useState(true);
  const [current, setCurrent] = useState([]);
  const [currentTotal, setCurrentTotal] = useState(0);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [mobileTab, setMobileTab] = useState('auto');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    loadAutoSettings();
    return undefined;
  }, [champId, champsLoading]);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadCurrent(searchQuery, 0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading]);

  const loadAutoSettings = async () => {
    if (!champId) {
      setAutoLeagues([]);
      setAutoLoading(false);
      return;
    }
    setAutoLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminDisqualificationSettings, { champ_id: champId });
      const list = extractList(response.data).filter(isAutoSettingConfigured);
      setAutoLeagues(list);
    } catch (error) {
      setAutoLeagues([]);
      setMessage({ text: getApiErrorMessage(error, t('dqLoadError')), type: 'error' });
    } finally {
      setAutoLoading(false);
    }
  };

  const loadCurrent = async (search, offset, append) => {
    if (!champId) {
      setCurrent([]);
      setCurrentTotal(0);
      setCurrentLoading(false);
      setLoadingMore(false);
      return;
    }
    if (append) setLoadingMore(true);
    else setCurrentLoading(true);
    try {
      const body = {
        champ_id: champId,
        limit: DISQ_PAGE_SIZE,
        offset,
      };
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminDisqualifications, body);
      const data = response.data || {};
      const items = extractList(data);
      const total = Number.isFinite(Number(data.total)) ? Number(data.total) : items.length;
      setCurrent((prev) => (append ? prev.concat(items) : items));
      setCurrentTotal(total);
    } catch (error) {
      if (!append) {
        setCurrent([]);
        setCurrentTotal(0);
      }
      setMessage({ text: getApiErrorMessage(error, t('dqLoadError')), type: 'error' });
    } finally {
      setCurrentLoading(false);
      setLoadingMore(false);
    }
  };

  const autoColumns = [
    {
      key: 'enabled',
      label: t('dqStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${Number(item.disqualEnabled) === 1 ? 'enabled' : 'disabled'}`}>
          {Number(item.disqualEnabled) === 1 ? t('dqEnabled') : t('dqDisabled')}
        </span>
      ),
    },
    {
      key: 'name',
      label: t('dqLeague'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="dq-league">
          <LeagueFlag flag={item.flag} />
          <span>{item.name || '—'}</span>
        </span>
      ),
    },
    {
      key: 'yc',
      label: t('dqYC'),
      className: 'st-cell-priority',
      render: (item) => (
        <span className="st-priority-badge">{item.disqualYC != null ? item.disqualYC : '—'}</span>
      ),
    },
  ];

  const currentColumns = [
    {
      key: 'player',
      label: t('dqPlayer'),
      className: 'st-cell-name',
      render: (item) => (
        <span className="dq-person">
          <span>{item.player_name || t('dqPlayerFallback', { id: item.player_id })}</span>
          {Number(item.is_auto) === 1 ? <span className="st-badge disabled">{t('dqAuto')}</span> : null}
        </span>
      ),
    },
    {
      key: 'tournament',
      label: t('dqTournament'),
      className: 'st-cell-address st-cell-muted',
      render: (item) => item.tournament || '—',
    },
    {
      key: 'games',
      label: t('dqGames'),
      className: 'st-cell-priority',
      render: (item) => (
        <span className="st-priority-badge">{formatDisqualificationTerm(item, t)}</span>
      ),
    },
  ];

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-page dq-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('dqTitle') }]} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="dq-mobile-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === 'auto'}
            className={`dq-mobile-tab ${mobileTab === 'auto' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('auto')}
          >
            {t('dqAutoTitle')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileTab === 'current'}
            className={`dq-mobile-tab ${mobileTab === 'current' ? 'is-active' : ''}`}
            onClick={() => setMobileTab('current')}
          >
            {t('dqCurrentTab')}
          </button>
        </div>

        <div className="st-scroll dq-scroll">
          <div className="dq-split">
            <section className={`st-block dq-panel ${mobileTab === 'auto' ? 'is-active-tab' : ''}`}>
              <h2 className="st-block-header">{t('dqAutoTitle')}</h2>
              <div className="dq-panel-body">
                {autoLoading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('dqLoading')}</p>
                  </div>
                ) : (
                  <ReorderableTable
                    tableClassName="st-table-dq-auto"
                    columns={autoColumns}
                    items={autoLeagues}
                    getRowKey={(item) => item.id}
                    reorderMode={false}
                    onItemsChange={() => {}}
                    getRowHref={(item) => (item.id ? `/league_disqualifications/auto/${item.id}` : null)}
                    empty={(
                      <div className="st-empty">
                        <FiSlash size={48} />
                        <h3>{t('dqAutoEmpty')}</h3>
                        <p>{t('dqAutoEmptyHint')}</p>
                      </div>
                    )}
                  />
                )}
              </div>
              <div className="st-actions dq-panel-actions">
                <IfCanWrite entity="disqualifications">
                  <Link to="/league_disqualifications/auto/new" className="st-btn st-btn-primary">
                    <FiPlus /> {t('dqAddAuto')}
                  </Link>
                </IfCanWrite>
              </div>
            </section>

            <section className={`st-block dq-panel ${mobileTab === 'current' ? 'is-active-tab' : ''}`}>
              <h2 className="st-block-header">{t('dqCurrentTitle')}</h2>
              <div className="dq-block-toolbar">
                <div className="st-search">
                  <FiSearch className="st-search-icon" />
                  <input
                    type="text"
                    className="st-search-input"
                    placeholder={t('dqSearchPh')}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                  {searchQuery ? (
                    <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                      ×
                    </button>
                  ) : null}
                </div>
                <div className="st-meta">
                  <strong>{t('dqFound', { count: currentTotal })}</strong>
                </div>
              </div>
              <div className="dq-panel-body">
                {currentLoading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('dqLoading')}</p>
                  </div>
                ) : (
                  <>
                    <ReorderableTable
                      tableClassName="st-table-dq-current"
                      columns={currentColumns}
                      items={current}
                      getRowKey={(item, index) => item.id || `auto-${item.player_id}-${item.league_id}-${index}`}
                      reorderMode={false}
                      onItemsChange={() => {}}
                      isRowLocked={false}
                      getRowHref={(item) => (
                        isManualDisqualification(item) && item.id
                          ? `/league_disqualifications/${item.id}`
                          : null
                      )}
                      onRowClick={(item) => {
                        if (isManualDisqualification(item)) return;
                        history.push({
                          pathname: `/league_disqualifications/current/${item.player_id}/${item.league_id || 0}`,
                          state: { item },
                        });
                      }}
                      empty={(
                        <div className="st-empty">
                          <FiSlash size={48} />
                          <h3>{t('dqCurrentEmpty')}</h3>
                          <p>{t('dqCurrentEmptyHint')}</p>
                        </div>
                      )}
                    />
                    {current.length < currentTotal ? (
                      <div className="dq-load-more">
                        <button
                          type="button"
                          className="st-btn st-btn-secondary"
                          disabled={loadingMore}
                          onClick={() => loadCurrent(searchQuery, current.length, true)}
                        >
                          {loadingMore ? t('dqLoading') : t('dqLoadMore')}
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div className="st-actions dq-panel-actions">
                <IfCanWrite entity="disqualifications">
                  <Link to="/league_disqualifications/new" className="st-btn st-btn-primary">
                    <FiPlus /> {t('dqAdd')}
                  </Link>
                </IfCanWrite>
              </div>
            </section>
          </div>
        </div>

        <div className="st-actions dq-page-actions">
          <IfCanWrite entity="disqualifications">
            {mobileTab === 'auto' ? (
              <Link to="/league_disqualifications/auto/new" className="st-btn st-btn-primary">
                <FiPlus />
                <span className="tr-btn-full">{t('dqAddAuto')}</span>
                <span className="tr-btn-short">{t('dqAddAutoShort')}</span>
              </Link>
            ) : (
              <Link to="/league_disqualifications/new" className="st-btn st-btn-primary">
                <FiPlus />
                <span className="tr-btn-full">{t('dqAdd')}</span>
                <span className="tr-btn-short">{t('dqAddShort')}</span>
              </Link>
            )}
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
